<template>
  <div>
    <ReportMobileFrame
      v-if="$vuetify.breakpoint.smAndDown"
      :loading="isLoading"
      :empty="!isLoading && data.length === 0"
      empty-text="No orders found for the selected range."
    >
      <template #filters>
        <div class="orders-mobile__dates">
          <MobileDateField
            label="Start Date"
            :value="dates[0]"
            @input="onMobileDateChange(0, $event)"
          />
          <MobileDateField
            label="End Date"
            align="right"
            :value="dates[1] || dates[0]"
            @input="onMobileDateChange(1, $event)"
          />
        </div>

        <v-select
          v-if="stores.length > 1"
          :items="stores"
          item-text="name"
          item-value="id"
          v-model="selectedStoreId"
          label="Store"
          outlined
          dense
          hide-details
          class="mt-2"
          @change="load"
        />

        <div class="orders-mobile__chips">
          <span class="orders-mobile__chips-label">Filters</span>
          <div class="orders-mobile__chip-row">
            <button
              v-for="opt in accountOptions"
              :key="opt"
              type="button"
              class="chip"
              :class="{ 'chip--active': selectedAccount === opt }"
              @click="setAccount(opt)"
            >{{ opt }}</button>
          </div>
        </div>
      </template>

      <div v-for="group in groupedByDate" :key="group.dateKey">
        <ReportGroupHeader :label="group.dateLabel" />
        <div v-for="order in group.orders" :key="order.id || order.orderRef">
          <ReportListRow clickable @click="toggleExpand(order)">
            <template #leading>
              <div
                class="payment-badge"
                :class="`payment-badge--${paymentVariant(order.account)}`"
              >
                <v-icon dark size="18">{{ paymentIcon(order.account) }}</v-icon>
              </div>
            </template>
            <template #title>£{{ order.total.toFixed(2) }}</template>
            <template #subtitle v-if="order.discount > 0">
              Discount £{{ order.discount.toFixed(2) }}
            </template>
            <template #trailing>
              <span class="orders-mobile__status">{{ (order.account || '').toUpperCase() }}</span>
              <span class="orders-mobile__time">{{ moment(order.orderPlacedAt).format('HH:mm') }}</span>
            </template>
          </ReportListRow>

          <div
            v-if="expandedOrderId === (order.id || order.orderRef)"
            class="orders-mobile__expanded"
          >
            <ul v-if="order.items && order.items.length > 0">
              <li v-for="(product, idx) in order.items" :key="idx">
                <span>{{ product.name }}</span>
                <span>£{{ product.cost.toFixed(2) }}</span>
              </li>
            </ul>
            <p v-else class="orders-mobile__expanded-empty">No item breakdown available.</p>

            <div v-if="order.account === 'Cash'" class="orders-mobile__expanded-actions">
              <v-btn small color="error" @click.stop="refundOrder(order)">
                <v-icon left small>mdi-cash-refund</v-icon>
                Refund
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </ReportMobileFrame>

    <template v-else>
    <v-toolbar flat class="toolbar-container mb-2" height='100'>
      <v-row>
        <v-col>
          <div class="toolbar-datepicker">
            <DateRangePicker :initialDates="dates" @blur="updateDates"  />
          </div>
        </v-col>

        <v-col v-if="stores.length > 1">
          <v-select :items="stores" item-text="name" v-model="selectedStoreId" item-value="id" />
        </v-col>

        <v-col>
          <v-select label="Account" :items="['All', 'Card', 'Cash']" v-model="selectedAccount"/>
        </v-col>
      </v-row>
    </v-toolbar>

    <v-row>
      <v-col>
        <v-btn block class="search-btn" type="button" color="success" v-on:click="load">
          SEARCH
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
       <v-data-table 
        :headers="headers" :items="data" 
        :loading="isLoading" :items-per-page="-1"
        :single-expand="true"
        show-expand
        
        item-key="orderRef" class="elevation-1 mt-5 flex-grow-1">

        <template v-slot:[`item.orderPlacedAt`]="{ item }">
          {{ moment(item.orderPlacedAt).format("DD/MM/yyyy HH:mm:ss") }}
        </template>

        <template v-slot:[`item.discount`]="{ item }">
          <span v-if="item.discount > 0">£{{ item.discount.toFixed(2) }}</span>
        </template>

        <template v-slot:[`item.total`]="{ item }">
          £{{ item.total.toFixed(2) }}
        </template>

        <template v-slot:[`item.account`]="{ item }">
          <span v-if="item.receiptPrinted">R:</span>
          {{ item.account }}
        </template>

        <template v-slot:[`item.option`]="{ item }">
          <v-btn v-if="item.account == 'Cash'" type="button" color="red" v-on:click="refundOrder(item)">
            REFUND
          </v-btn>
        </template>

        <template v-slot:expanded-item="{ item }">
          <td :colspan="headers.length">
            <div class="sub-total">
              <ul>
                <li v-for="product in item.items" :key="product.id">{{ product.name }} - £{{ product.cost.toFixed(2) }}</li>
              </ul>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-row>
    </template>

  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue';
import ReportMobileFrame from '@/components/reports/ReportMobileFrame.vue';
import ReportGroupHeader from '@/components/reports/ReportGroupHeader.vue';
import ReportListRow from '@/components/reports/ReportListRow.vue';
import MobileDateField from '@/components/reports/MobileDateField.vue';
import moment from 'moment';
import { reportService } from '@/services';
import { userService } from '@/services';
import router from "../../router";

export default {

  name: 'Orders',
  components: {
    DateRangePicker,
    ReportMobileFrame,
    ReportGroupHeader,
    ReportListRow,
    MobileDateField
  },
  data: vm => ({
    dates: [new Date().toISOString().substr(0, 10)],
    isLoading: false,

    stores: [],
    selectedStoreId: null,
    selectedAccount: "All",
    accountOptions: ['All', 'Card', 'Cash'],
    expandedOrderId: null,

    singleExpand: false,
    data: [],
    headers: [
      // { text: 'Order Ref', align: 'start', sortable: true, filterable: true, value: 'orderRef' },
      { text: 'Order Placed At', align: 'start', sortable: true, filterable: true, value: 'orderPlacedAt' },
      { text: 'Discount', align: 'start', sortable: true, filterable: true, value: 'discount' },
      { text: 'Total', align: 'start', sortable: true, filterable: true, value: 'total' },
      { text: 'Account', align: 'start', sortable: true, filterable: true, value: 'account' },
      { text: 'Option', align: 'start', sortable: false, filterable: false, value: 'option' },
    ],
  }),
  async created () {
    if (userService.isStaff()) {
      router.push('/products');
    }

    const { startDate, endDate } = this.$route.query;
    if (startDate) {
      this.dates = endDate && endDate !== startDate ? [startDate, endDate] : [startDate, startDate];
    }

    // Load our stores
    this.stores = await reportService.getStores();
    this.stores.unshift({
      id: null,
      name: 'All'
    });

    this.load();
  },
  methods: {
    updateDates(newDates) {
      this.dates = newDates;
    },
    async load() {
      var startDate = this.dates[0];
      var endDate = this.dates[0];
      if (this.dates.length > 1)
        endDate = this.dates[1];

      this.isLoading = true;
      this.data = await reportService.getOrderReport(startDate, endDate, this.selectedStoreId, this.selectedAccount);

      this.isLoading = false;
    },
    async refundOrder(order) {
      var response = confirm("Are you sure you want to refund this order?");
      if (!response)
        return;

      var idx = this.data.indexOf(order);
      await reportService.deleteOrder(order.id);
      this.data.splice(idx, 1)
    },
    onMobileDateChange(index, value) {
      if (!value) return;
      const next = this.dates.slice();
      while (next.length <= index) next.push(value);
      next[index] = value;
      if (index === 0) next[1] = value;
      this.dates = next;
      this.load();
    },
    setAccount(option) {
      this.selectedAccount = option;
      this.load();
    },
    toggleExpand(order) {
      const id = order.id || order.orderRef;
      this.expandedOrderId = this.expandedOrderId === id ? null : id;
    },
    paymentVariant(account) {
      switch ((account || '').toLowerCase()) {
        case 'card': return 'card';
        case 'cash': return 'cash';
        case 'split': return 'split';
        default: return 'other';
      }
    },
    paymentIcon(account) {
      switch ((account || '').toLowerCase()) {
        case 'card': return 'mdi-credit-card-outline';
        case 'cash': return 'mdi-cash';
        case 'split': return 'mdi-call-split';
        default: return 'mdi-receipt-text-outline';
      }
    }
  },
  computed: {
    groupedByDate() {
      const sorted = this.data.slice().sort((a, b) => {
        return new Date(b.orderPlacedAt) - new Date(a.orderPlacedAt);
      });

      const groups = new Map();
      for (const order of sorted) {
        const m = this.moment(order.orderPlacedAt);
        const key = m.format('YYYY-MM-DD');
        if (!groups.has(key)) {
          groups.set(key, {
            dateKey: key,
            dateLabel: m.format('DD MMM YYYY'),
            orders: []
          });
        }
        groups.get(key).orders.push(order);
      }

      return Array.from(groups.values());
    }
  }
}
</script>

<style lang="scss" scoped>
  .toolbar-container {
    background: #FFF !important;
    padding: 10px;
    height: auto;
    border-radius: 8px;

    border: 1px solid lighten(#7B5CD8, 30);
  }

  .toolbar-content {
    display: flex;
    flex-direction: row;
    width: 100%;

    .toolbar-datepicker {
      width: 250px;
    }
  }

  .sub-total {
    ul {
      width: 100%;
      list-style-type: none;
      margin: 0;
      padding: 10px 10px;
    }
  }

  .orders-mobile__dates {
    display: grid;
    gap: 8px;
    grid-template-columns: 1fr 1fr;
  }

  .date-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .date-field span {
    color: var(--app-text);
    font-size: 12px;
    font-weight: 700;
    text-align: center;
  }

  .date-field input {
    background: var(--app-surface);
    border: 1px solid var(--app-border-strong);
    border-radius: var(--app-radius);
    color: var(--app-text);
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 9px 10px;
    text-align: center;
    width: 100%;
  }

  .date-field input:focus {
    border-color: var(--app-primary);
    outline: none;
  }

  .orders-mobile__chips {
    margin-top: 10px;
  }

  .orders-mobile__chips-label {
    color: var(--app-text-muted);
    display: block;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 6px;
  }

  .orders-mobile__chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .chip {
    background: var(--app-surface);
    border: 1px solid var(--app-border-strong);
    border-radius: 999px;
    color: var(--app-text);
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    padding: 7px 16px;
    transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
  }

  .chip:active {
    transform: scale(0.97);
  }

  .chip--active {
    background: var(--app-primary);
    border-color: var(--app-primary);
    color: #fff;
  }

  .payment-badge {
    align-items: center;
    border-radius: 6px;
    display: flex;
    height: 30px;
    justify-content: center;
    width: 42px;
  }

  .payment-badge--card {
    background: var(--app-primary);
  }

  .payment-badge--cash {
    background: var(--app-success);
  }

  .payment-badge--split {
    background: var(--app-warning);
  }

  .payment-badge--other {
    background: var(--app-text-muted);
  }

  .orders-mobile__status {
    background: #d1fae5;
    border-radius: 999px;
    color: #065f46;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.4px;
    padding: 3px 10px;
    text-transform: uppercase;
  }

  .orders-mobile__time {
    color: var(--app-text-muted);
    font-size: 13px;
    font-weight: 600;
  }

  .orders-mobile__expanded {
    background: #f8fafc;
    border-bottom: 1px solid var(--app-border);
    padding: 12px 16px 14px;
  }

  .orders-mobile__expanded ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .orders-mobile__expanded li {
    color: var(--app-text);
    display: flex;
    font-size: 13px;
    justify-content: space-between;
    padding: 4px 0;
  }

  .orders-mobile__expanded-empty {
    color: var(--app-text-muted);
    font-size: 13px;
    margin: 0;
  }

  .orders-mobile__expanded-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }
</style>
